/**
 * SEO-optimized alt text generator for vehicle images
 * Creates descriptive, keyword-rich alt text for Google Images indexing
 */

interface VehicleImageData {
  make: string;
  model: string;
  year: number;
  price?: number;
  currency?: string;
  location_city?: string;
  location_region?: string;
  condition?: string;
  color?: string;
  fuel_type?: string;
}

/**
 * Generate SEO-optimized alt text for vehicle listing images
 * Format: "{Make} {Model} {Year} {Color} {Condition} - {Price} {Currency} în {Location}"
 * Max 125 characters for optimal SEO
 */
export function generateVehicleImageAlt(
  vehicle: VehicleImageData,
  imageIndex: number = 0
): string {
  const { make, model, year, price, currency, location_city, color, condition } = vehicle;
  
  // Base description
  let alt = `${make} ${model} ${year}`;
  
  // Add color if available
  if (color) {
    alt += ` ${color}`;
  }
  
  // Add condition
  if (condition) {
    const conditionText = condition.toLowerCase() === 'nou' || condition.toLowerCase() === 'new' 
      ? 'nou' 
      : 'second hand';
    alt += ` ${conditionText}`;
  }
  
  // Add view type based on image index
  const viewTypes = [
    'vedere frontală',
    'vedere laterală',
    'interior',
    'vedere spate',
    'detalii motor',
    'bord',
    'portbagaj',
    'roți',
    'detalii',
    'vedere completă'
  ];
  const viewType = viewTypes[imageIndex % viewTypes.length];
  
  // Add price if available
  if (price && price > 0) {
    const priceFormatted = price.toLocaleString('ro-RO');
    alt += ` - ${priceFormatted} ${currency || 'EUR'}`;
  }
  
  // Add location if first image
  if (imageIndex === 0 && location_city) {
    alt += ` în ${location_city}`;
  } else if (imageIndex > 0) {
    alt += ` - ${viewType}`;
  }
  
  // Truncate to 125 characters for SEO best practices
  if (alt.length > 125) {
    alt = alt.substring(0, 122) + '...';
  }
  
  return alt;
}

/**
 * Generate alt text for thumbnail/card images
 * Shorter format optimized for list views
 */
export function generateThumbnailAlt(
  vehicle: VehicleImageData
): string {
  const { make, model, year, price, currency, location_city } = vehicle;
  
  let alt = `${make} ${model} ${year}`;
  
  if (price && price > 0) {
    const priceFormatted = price.toLocaleString('ro-RO');
    alt += ` - ${priceFormatted} ${currency || 'EUR'}`;
  }
  
  if (location_city) {
    alt += ` în ${location_city}`;
  }
  
  return alt;
}

/**
 * Generate alt text for gallery images with position context
 */
export function generateGalleryImageAlt(
  vehicle: VehicleImageData,
  imageIndex: number,
  totalImages: number
): string {
  const { make, model, year } = vehicle;
  const position = imageIndex + 1;
  
  const viewDescriptions: Record<number, string> = {
    0: 'vedere principală',
    1: 'profil lateral',
    2: 'interior complet',
    3: 'vedere din spate',
    4: 'compartiment motor',
    5: 'tablou de bord',
    6: 'spațiu portbagaj',
    7: 'jante și anvelope',
    8: 'detalii exterioare',
    9: 'detalii interioare'
  };
  
  const viewDesc = viewDescriptions[imageIndex] || `imagine ${position} din ${totalImages}`;
  
  return `${make} ${model} ${year} - ${viewDesc}`;
}
